/**
 * `ViewToggle` — the global `Human | Code` segmented control
 * (ADR-0163 §3). Active segment matches the Run button treatment
 * (lime background, ink text). Touch targets ≥44px on coarse
 * pointers via min-height; visually compact on desktop.
 */

import { useC } from '../../../lib/theme.js';
import type { ViewMode } from '../hooks/useViewMode.js';

const sansFont = "'Geist', sans-serif";

export interface ViewToggleProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}

const SEGMENTS: readonly { value: ViewMode; label: string }[] = [
  { value: 'human', label: 'Human' },
  { value: 'code', label: 'Code' },
];

export function ViewToggle({ mode, onChange }: ViewToggleProps): JSX.Element {
  const C = useC();
  return (
    <div
      role="tablist"
      aria-label="Response view"
      className="flex items-stretch"
      style={{ border: `1px solid ${C.border}`, borderRadius: 2, overflow: 'hidden' }}
    >
      {SEGMENTS.map((s) => {
        const active = mode === s.value;
        return (
          <button
            key={s.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(s.value)}
            className="px-3"
            style={{
              minHeight: 36,
              minWidth: 44,
              background: active ? C.lime : 'transparent',
              color: active ? C.ink : C.muted,
              fontFamily: sansFont,
              fontSize: 11,
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
